import { compare } from 'compare-versions';

/**
 * Parse a dotted firmware version from device settings (e.g. `1.6.2` from
 * `20250101-120000/1.6.2-githash`). Used by new-layout BLE UI only.
 */
export const parseBleFwVersion = (settings: any): string | null => {
	const raw =
		settings?.DeviceInfo?.ver ||
		settings?.sys?.device?.fw_id ||
		settings?.DeviceInfo?.fw_id;
	return raw?.match(/(\d{1,}\.){1,}\d{1,}/)?.[0] ?? null;
};

export const isBleFwAtLeast = (settings: any, version: string): boolean => {
	const fwVersion = parseBleFwVersion(settings);
	if (!fwVersion) return false;
	try {
		return compare(fwVersion, version, '>=');
	} catch {
		return false;
	}
};

/**
 * True when firmware exposes the 2.0.0 BLE pairing contract:
 * `ble.enable` removed, `Ble.StartPairing` / `ListPairedDevices` / status `flags`.
 *
 * Capability fallback: config has `ble.rpc` and no `ble.enable`.
 * Do not use `isFwAfter200` (that still gates on 1.8.9).
 */
export const hasBlePairingApi = (settings: any): boolean => {
	if (isBleFwAtLeast(settings, '2.0.0')) return true;
	const ble = settings?.ble;
	return Boolean(ble && typeof ble.enable === 'undefined' && ble.rpc);
};

/**
 * Observer / CloudRelay scan is automatic (no `observer.enable` toggle).
 * True from 1.5.0, pairing FW, or when the device already reports always-on.
 */
export const isBleObserverAutomatic = (settings: any, status?: any): boolean => {
	if (hasBlePairingApi(settings) || isBleFwAtLeast(settings, '1.5.0')) return true;
	return (
		typeof settings?.ble?.observer?.always_on !== 'undefined' ||
		typeof status?.sys?.btrelay_rev !== 'undefined'
	);
};

/** Strip BLE address type suffix (`AA:BB:CC:DD:EE:FF,p` → `AA:BB:CC:DD:EE:FF`). */
export const stripBleAddrTypeSuffix = (value?: string | null): string => {
	if (!value) return '';
	return value.split(',')[0] ?? '';
};

/** Normalises a BLE MAC to uppercase colon-separated form (AA:BB:CC:DD:EE:FF). */
export const formatBleMacAddress = (mac: string): string => {
	if (!mac) return '';
	const hex = mac.replace(/[^0-9a-fA-F]/g, '').toUpperCase();
	if (hex.length !== 12) return '';
	return hex.match(/.{1,2}/g)?.join(':') ?? '';
};

export type BleMacSources = {
	statusMac?: string;
	statusAddr?: string;
	rpcMac?: string;
	rpcAddr?: string;
};

/**
 * First usable MAC from status/RPC sources. Status `addr` wins over `mac`.
 */
export const pickBleMacFromSources = (sources: BleMacSources): string => {
	const candidates = [
		stripBleAddrTypeSuffix(sources.statusAddr),
		stripBleAddrTypeSuffix(sources.statusMac),
		stripBleAddrTypeSuffix(sources.rpcAddr),
		stripBleAddrTypeSuffix(sources.rpcMac),
	];
	for (const candidate of candidates) {
		const formatted = formatBleMacAddress(candidate);
		if (formatted) return formatted;
	}
	return '';
};
